import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useSelector, useDispatch } from 'react-redux';
import { getArtistCampaigns } from '../module/features/Artist';

const NotificationContext = createContext(null);

export const useNotifications = () => useContext(NotificationContext);

export const useAdminNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useAdminNotifications must be used within a NotificationProvider');
  }

  return {
    notifications: context.adminNotifications,
    unreadCount: context.adminUnreadCount,
    isLoading: context.adminIsLoading,
    error: context.adminError,
    isConnected: context.isConnected,
    fetchNotifications: context.fetchAdminNotifications,
    markAsRead: context.markAdminAsRead,
    markAllAsRead: context.markAllAdminAsRead,
    adminPage: context.adminPage,
    adminTotalPages: context.adminTotalPages,
  };
};

export const NotificationProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const [adminNotifications, setAdminNotifications] = useState([]);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const [adminIsLoading, setAdminIsLoading] = useState(true);
  const [adminError, setAdminError] = useState(null);
  const [adminPage, setAdminPage] = useState(1);
  const [adminTotalPages, setAdminTotalPages] = useState(1);

  const dispatch = useDispatch();

  const auth0User = useSelector(state => state.authenticationSlice?.auth0User);
  const isAdmin = useSelector(state => state.authenticationSlice?.isAdmin);

  const userId = auth0User?.sub;

  const baseURL =
    process.env.REACT_APP_BACKEND_URI || process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    if (!userId) {
      return;
    }

    const newSocket = io(baseURL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: true,
      query: { userId, isAdmin },
    });

    setSocket(newSocket);
    window.socket = newSocket;

    newSocket.on('connect', () => {
      setIsConnected(true);

      newSocket.emit('register', { userId, isAdmin }, response => {
        if (!response?.success) {
          setError('Failed to register with notification service');
        }
      });
    });

    newSocket.on('connect_error', () => {
      setIsConnected(false);
      setError('Notification service connection failed');
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('reconnect', () => {
      setIsConnected(true);
      newSocket.emit('register', { userId, isAdmin });
    });

    return () => {
      if (newSocket) {
        newSocket.disconnect();
        window.socket = null;
      }
    };
  }, [userId, baseURL, isAdmin]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = notification => {
      setUnreadCount(prev => prev + 1);

      if (!isAdmin) {
        dispatch(getArtistCampaigns());
      }

      const fetchNotificationsAfterSocket = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(`${baseURL}/notifications/${userId}`);

          if (
            response.data &&
            response.data.data &&
            response.data.data.notifications &&
            response.data.data.notifications.length
          ) {
            setNotifications(response.data.data.notifications);
            setUnreadCount(response.data.data.unreadCount || 0);
            setError(null);
          }
        } catch (err) {
          setError('Failed to fetch notifications');
        } finally {
          setIsLoading(false);
        }
      };

      fetchNotificationsAfterSocket();
    };

    const handleAdminNotification = () => {
      setAdminUnreadCount(prev => prev + 1);
      fetchAdminNotifications();
    };

    const handleCampaignChange = () => {
      if (isAdmin) {
        reloadAdminCampaigns();
      } else {
        dispatch(getArtistCampaigns());
      }
    };

    const reloadAdminCampaigns = () => {
      dispatch(getArtistCampaigns());
      const campaignUpdateEvent = new CustomEvent('admin-campaigns-update');
      window.dispatchEvent(campaignUpdateEvent);
    };

    socket.on('newNotification', handleNewNotification);
    socket.on('adminNotification', handleAdminNotification);
    socket.on('campaignCreated', handleCampaignChange);
    socket.on('campaignUpdated', handleCampaignChange);
    socket.on('campaignStatusUpdated', handleCampaignChange);

    socket.on('statusUpdate', () => {
      if (isAdmin) {
        reloadAdminCampaigns();
      } else {
        dispatch(getArtistCampaigns());
      }
    });

    return () => {
      socket.off('newNotification', handleNewNotification);
      socket.off('adminNotification', handleAdminNotification);
      socket.off('campaignCreated', handleCampaignChange);
      socket.off('campaignUpdated', handleCampaignChange);
      socket.off('campaignStatusUpdated', handleCampaignChange);
      socket.off('statusUpdate');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, isAdmin, dispatch]);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const fetchNotifications = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${baseURL}/notifications/${userId}`);

        if (
          response.data &&
          response.data.data &&
          response.data.data.notifications &&
          response.data.data.notifications.length
        ) {
          setNotifications(response.data.data.notifications);
          setUnreadCount(response.data.data.unreadCount || 0);
          setError(null);
        }
      } catch (err) {
        setError('Failed to fetch notifications');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [userId, baseURL, isConnected]);

  useEffect(() => {
    if (!isAdmin || !isConnected) {
      setAdminIsLoading(false);
      return;
    }

    fetchAdminNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isConnected, baseURL]);

  const fetchAdminNotifications = useCallback(
    async ({ page = 1, limit = 20, append = false } = {}) => {
      if (!isAdmin) {
        return;
      }

      if (!append) {
        setAdminIsLoading(true);
      }
      setAdminError(null);
      try {
        const safePage = Number.isFinite(page) && page > 0 ? page : 1;
        const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 20;

        const response = await axios.get(`${baseURL}/admin-notifications`, {
          params: { page: safePage, limit: safeLimit },
        });

        if (response.data?.data?.notifications) {
          setAdminNotifications(prev =>
            append
              ? [...prev, ...response.data.data.notifications]
              : response.data.data.notifications
          );
          setAdminUnreadCount(response.data.data.unreadCount || 0);
          setAdminPage(response.data.data.page || safePage);
          setAdminTotalPages(response.data.data.totalPages || 1);
        } else {
          setAdminNotifications([]);
          setAdminUnreadCount(0);
          setAdminPage(1);
          setAdminTotalPages(1);
        }
      } catch (err) {
        setAdminError('Failed to fetch admin notifications');
      } finally {
        setAdminIsLoading(false);
      }
    },
    [isAdmin, baseURL]
  );

  const markAsRead = useCallback(
    async notificationId => {
      if (!userId) return;

      try {
        await axios.patch(`${baseURL}/notifications/${notificationId}/read`);

        setNotifications(prev =>
          prev.map(notification =>
            notification._id === notificationId ? { ...notification, isRead: true } : notification
          )
        );

        const notification = notifications.find(n => n._id === notificationId);
        if (notification && !notification.isRead) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      } catch (err) {
        setError('Failed to mark notification as read');
      }
    },
    [userId, baseURL, notifications]
  );

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    try {
      await axios.patch(`${baseURL}/notifications/${userId}/read-all`);

      setNotifications(prev => prev.map(notification => ({ ...notification, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      setError('Failed to mark all notifications as read');
    }
  }, [userId, baseURL]);

  const markAdminAsRead = useCallback(
    async notificationId => {
      if (!isAdmin) return;

      try {
        await axios.patch(`${baseURL}/admin-notifications/${notificationId}/read`);

        setAdminNotifications(prev =>
          prev.map(notification =>
            notification._id === notificationId ? { ...notification, isRead: true } : notification
          )
        );

        const notification = adminNotifications.find(n => n._id === notificationId);
        if (notification && !notification.isRead) {
          setAdminUnreadCount(prev => Math.max(0, prev - 1));
        }
      } catch (err) {
        setAdminError('Failed to mark admin notification as read');
      }
    },
    [isAdmin, baseURL, adminNotifications]
  );

  const markAllAdminAsRead = useCallback(async () => {
    if (!isAdmin) return;

    try {
      await axios.patch(`${baseURL}/admin-notifications/read-all`);

      setAdminNotifications(prev => prev.map(notification => ({ ...notification, isRead: true })));
      setAdminUnreadCount(0);
    } catch (err) {
      setAdminError('Failed to mark all admin notifications as read');
    }
  }, [isAdmin, baseURL]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      error,
      isConnected,
      markAsRead,
      markAllAsRead,
      adminNotifications,
      adminUnreadCount,
      adminIsLoading,
      adminError,
      adminPage,
      adminTotalPages,
      fetchAdminNotifications,
      markAdminAsRead,
      markAllAdminAsRead,
      socket,
      isAdmin,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      error,
      isConnected,
      markAsRead,
      markAllAsRead,
      adminNotifications,
      adminUnreadCount,
      adminIsLoading,
      adminError,
      adminPage,
      adminTotalPages,
      fetchAdminNotifications,
      markAdminAsRead,
      markAllAdminAsRead,
      socket,
      isAdmin,
    ]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationContext;
